import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getCurrencySetting } from '@/lib/currency';
import { DEFAULT_TIMEZONE } from '@/lib/timezones';

type SocialLinkValue = string | null | undefined | {
  enabled?: boolean;
  url?: string | null;
};

export interface SocialLinks {
  instagram?: SocialLinkValue;
  facebook?: SocialLinkValue;
  linkedin?: SocialLinkValue;
}

export interface CompanyInfoFull {
  company_name: string;
  logo_url: string | null;
  footer_tagline: string | null;
  phone_number: string | null;
  service_areas: string[];
  social_links: SocialLinks | null;
}

export interface CompanyInfoBrief {
  company_name: string;
  logo_url: string | null;
}

/**
 * Full company_info row for the Footer component (tagline, social links, etc.).
 * The original query used `.order('updated_at').limit(1).maybeSingle()`.
 */
export function useCompanyInfoFull(orgId: string | undefined) {
  return useQuery({
    queryKey: ['company-info-full', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<CompanyInfoFull | null> => {
      const { data, error } = await supabase
        .from('company_info')
        .select('company_name, logo_url, footer_tagline, phone_number, service_areas, social_links')
        .eq('organization_id', orgId!)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (!data) return null;
      return {
        ...data,
        social_links: (data.social_links as SocialLinks | null) ?? null,
      };
    },
  });
}

/**
 * Brief company_info row (name + logo only) for the Navbar component.
 * The original query used `.order('updated_at').limit(1).maybeSingle()`.
 */
export function useCompanyInfoBrief(orgId: string | undefined) {
  return useQuery({
    queryKey: ['company-info-brief', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<CompanyInfoBrief | null> => {
      const { data, error } = await supabase
        .from('company_info')
        .select('company_name, logo_url')
        .eq('organization_id', orgId!)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      return data ?? null;
    },
  });
}

/**
 * Minimal service_areas array from company_info. Used by the About page.
 * The original query used `.order('updated_at').limit(1).maybeSingle()`.
 */
export function useServiceAreas(orgId: string | undefined) {
  return useQuery({
    queryKey: ['service-areas', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from('company_info')
        .select('service_areas')
        .eq('organization_id', orgId!)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      return data?.service_areas ?? [];
    },
  });
}

// ─── Admin types & helpers ────────────────────────────────────────────────────

export interface AdminSocialLink {
  enabled: boolean;
  url: string;
}

export interface AdminSocialLinks {
  instagram: AdminSocialLink;
  facebook: AdminSocialLink;
  tiktok: AdminSocialLink;
  linkedin: AdminSocialLink;
  whatsapp: AdminSocialLink;
}

export type AreaRadii = Record<string, number>; // areaName -> km

export interface CompanyInfoAdmin {
  id: string;
  company_name: string;
  logo_url: string | null;
  footer_tagline: string | null;
  phone_number: string | null;
  currency_code: string;
  currency_name: string;
  currency_symbol: string;
  service_areas: string[];
  service_area_radii: AreaRadii;
  social_links: AdminSocialLinks;
  timezone: string;
  /** Owner's choice; only honoured on the Business plan (see org_shows_powered_by). */
  hide_powered_by: boolean;
}

type RawCompanyInfoAdmin = Partial<CompanyInfoAdmin> & {
  id: string;
  company_name: string;
  social_links?: unknown;
  service_area_radii?: unknown;
};

const defaultAdminSocialLinks: AdminSocialLinks = {
  instagram: { enabled: false, url: '' },
  facebook: { enabled: false, url: '' },
  tiktok: { enabled: false, url: '' },
  linkedin: { enabled: false, url: '' },
  whatsapp: { enabled: false, url: '' },
};

export const normaliseCompanyInfoAdmin = (data: RawCompanyInfoAdmin): CompanyInfoAdmin => {
  const socialLinks = data.social_links
    ? { ...defaultAdminSocialLinks, ...(data.social_links as unknown as AdminSocialLinks) }
    : defaultAdminSocialLinks;

  return {
    ...data,
    currency_code: data.currency_code || 'GBP',
    currency_name: data.currency_name || getCurrencySetting(data.currency_code).name,
    currency_symbol: data.currency_symbol || getCurrencySetting(data.currency_code).symbol,
    service_areas: data.service_areas || [],
    service_area_radii: (data.service_area_radii as AreaRadii) || {},
    social_links: socialLinks,
    timezone: data.timezone || DEFAULT_TIMEZONE,
    hide_powered_by: Boolean(data.hide_powered_by),
  };
};

/** Full company_info row for the admin CompanyInfoAdmin page. */
export function useCompanyInfoAdminData(
  orgId: string | null | undefined,
  orgLoading: boolean,
) {
  return useQuery({
    queryKey: ['company-info-admin', orgId],
    enabled: Boolean(orgId) && !orgLoading,
    queryFn: async (): Promise<CompanyInfoAdmin | null> => {
      const { data, error } = await supabase
        .from('company_info')
        .select('*')
        .eq('organization_id', orgId!)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      return normaliseCompanyInfoAdmin(data as RawCompanyInfoAdmin);
    },
  });
}

export interface SaveCompanyInfoInput {
  orgId: string;
  companyInfo: CompanyInfoAdmin;
}

export function useSaveCompanyInfo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orgId, companyInfo }: SaveCompanyInfoInput) => {
      const { error } = await supabase
        .from('company_info')
        .update({
          company_name: companyInfo.company_name,
          logo_url: companyInfo.logo_url,
          footer_tagline: companyInfo.footer_tagline,
          phone_number: companyInfo.phone_number,
          currency_code: companyInfo.currency_code,
          currency_name: companyInfo.currency_name,
          currency_symbol: companyInfo.currency_symbol,
          service_areas: companyInfo.service_areas,
          service_area_radii: companyInfo.service_area_radii,
          social_links: JSON.parse(JSON.stringify(companyInfo.social_links)),
          timezone: companyInfo.timezone,
          hide_powered_by: companyInfo.hide_powered_by,
        } as never)
        .eq('id', companyInfo.id)
        .eq('organization_id', orgId);

      if (error) throw error;

      // Keep organizations.name in sync — it's the source of truth for the admin
      // sidebar, OrgContext, and the public org-derived UI, none of which read
      // company_info.company_name. Without this the rename only lands on this page.
      const { error: orgError } = await supabase
        .from('organizations')
        .update({ name: companyInfo.company_name })
        .eq('id', orgId);

      if (orgError) throw orgError;
    },
    onSuccess: (_result, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: ['company-info-admin', orgId] });
      queryClient.invalidateQueries({ queryKey: ['company-info-full', orgId] });
      queryClient.invalidateQueries({ queryKey: ['company-info-brief', orgId] });
      queryClient.invalidateQueries({ queryKey: ['service-areas', orgId] });
      queryClient.invalidateQueries({ queryKey: ['org-profile'] });
      queryClient.invalidateQueries({ queryKey: ['org-shows-powered-by', orgId] });
    },
  });
}
